import { useEffect, useState, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { fetchMemberMessages, sendMemberMessage } from '../api';

// Per-member two-way conversation modal. Admins email or text one member and see
// their replies inline (inbound SMS is captured live; inbound email once
// SendGrid Inbound Parse is configured). Rendered as an overlay from AdminMembers.

function fmtWhen(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function MemberConversation({ member, onClose }) {
  const { instance } = useMsal();
  const account = instance.getActiveAccount();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [channel, setChannel] = useState(member?.email ? 'email' : 'sms');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState(null);

  const threadRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchMemberMessages(instance, account, member.id);
        if (cancelled) return;
        if (res.ok) setMessages(res.data.messages || []);
        else setError(res.data?.message || 'Could not load the conversation.');
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id]); // instance/account stable; re-fetch only if member changes

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMemberMessages(instance, account, member.id);
      if (res.ok) setMessages(res.data.messages || []);
      else setError(res.data?.message || 'Could not load the conversation.');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  // Auto-scroll the thread to the newest message.
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const canEmail = Boolean(member?.email);
  const canSms = Boolean(member?.phone_e164);

  const send = async (e) => {
    e.preventDefault();
    setSendMsg(null);
    if (!body.trim()) { setSendMsg({ type: 'error', text: 'Message body is empty.' }); return; }
    if (channel === 'email' && !subject.trim()) { setSendMsg({ type: 'error', text: 'Add a subject for email.' }); return; }
    setSending(true);
    try {
      const res = await sendMemberMessage(instance, account, member.id, {
        channel,
        subject: channel === 'email' ? subject.trim() : undefined,
        body: body.trim(),
      });
      if (res.ok) {
        setSendMsg({ type: 'success', text: `${channel === 'email' ? 'Email' : 'Text'} sent.` });
        setBody('');
        setSubject('');
        await refresh();
      } else {
        setSendMsg({ type: 'error', text: res.data?.error || res.data?.message || 'Could not send.' });
      }
    } catch (e) {
      setSendMsg({ type: 'error', text: e.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal conv-modal" onClick={(e) => e.stopPropagation()}>
        <div className="conv-head">
          <div>
            <h3>{member.display_name || member.email}</h3>
            <div className="muted conv-contacts">
              {member.email && <span>{member.email}</span>}
              {member.phone_e164 && <span> · {member.phone_e164}</span>}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="conv-thread" ref={threadRef}>
          {loading ? <p className="muted">Loading…</p> : error ? (
            <p className="error-text">{error}</p>
          ) : messages.length === 0 ? (
            <p className="muted conv-empty">No messages yet. Start the conversation below.</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={`conv-bubble conv-${m.direction}`}>
                <div className="conv-bubble-meta">
                  <span className={`conv-chan conv-chan-${m.channel}`}>{m.channel === 'sms' ? 'SMS' : 'Email'}</span>
                  {m.direction === 'outbound'
                    ? <span>{m.sender_name ? `Sent by ${m.sender_name}` : 'Sent'}</span>
                    : <span>Reply</span>}
                  <span className="muted">{fmtWhen(m.created_at)}</span>
                  {m.status === 'failed' && <span className="conv-failed">failed</span>}
                </div>
                {m.subject && <div className="conv-subject">{m.subject}</div>}
                <div className="conv-body">{m.body}</div>
              </div>
            ))
          )}
        </div>

        <form className="conv-compose" onSubmit={send}>
          <div className="conv-channel-row">
            <label className={`conv-channel${channel === 'email' ? ' is-active' : ''}${!canEmail ? ' is-disabled' : ''}`}>
              <input type="radio" name="channel" value="email" checked={channel === 'email'}
                disabled={!canEmail} onChange={() => setChannel('email')} />
              Email
            </label>
            <label className={`conv-channel${channel === 'sms' ? ' is-active' : ''}${!canSms ? ' is-disabled' : ''}`}>
              <input type="radio" name="channel" value="sms" checked={channel === 'sms'}
                disabled={!canSms} onChange={() => setChannel('sms')} />
              Text (SMS)
            </label>
          </div>
          {channel === 'email' && (
            <input type="text" className="conv-subject-input" placeholder="Subject"
              value={subject} maxLength={300} onChange={(e) => setSubject(e.target.value)} />
          )}
          <textarea rows={3} placeholder={channel === 'sms' ? 'Type a text…' : 'Type an email…'}
            value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="conv-send-row">
            <button type="submit" disabled={sending}>{sending ? 'Sending…' : `Send ${channel === 'sms' ? 'text' : 'email'}`}</button>
            {sendMsg && <span className={sendMsg.type === 'error' ? 'error-text' : 'success-text'}>{sendMsg.text}</span>}
          </div>
        </form>
      </div>
    </div>
  );
}
